import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../types/ServiceStoreTypes";
import { findOrThrow } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "./sessionGuard";

export interface Client {
  id: string;
  name: string;
  code: string;
  type?: string;
  parentClientId?: string | null;
  isMultiCompany?: boolean;
  status?: string;
  industry?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  taxId?: string | null;
  fiscalYearEnd?: string;
  defaultCurrency?: string;
  notes?: string | null;
  logoUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const STORAGE_KEY = "intrasync-selected-client-id";

class ClientStore {
  rootStore: RootStore;
  selectedClientId: string | null = null;
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Restore from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      this.selectedClientId = stored;
    }
  }

  /** The registered `clients` ServiceStore (see serviceStores/system.stores.ts). */
  private get service(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * The tenant client list. Per the MobX rule, entities live in the ServiceStore
   * cache (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get clients(): Client[] {
    return (this.service?.entities ?? []) as unknown as Client[];
  }

  get selectedClient(): Client | null {
    if (!this.selectedClientId) return null;
    return this.clients.find((c) => c.id === this.selectedClientId) || null;
  }

  get hasClient(): boolean {
    return !!this.selectedClientId;
  }

  /**
   * Called by RootStore.purgeUserData on sign-out: the selected client, and
   * the copy persisted in this browser, were the previous person's pick.
   */
  reset() {
    this.setSelectedClientId(null);
    this.loading = false;
    this.error = null;
  }

  setSelectedClientId(clientId: string | null) {
    this.selectedClientId = clientId;
    if (clientId) {
      localStorage.setItem(STORAGE_KEY, clientId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  async fetchClients() {
    this.loading = true;
    this.error = null;
    try {
      // The `clients` collection is *the* set of tenants, not a per-tenant
      // collection, so it must NOT be scoped by the selected clientId. A plain
      // find populates the ServiceStore's entity cache (which `clients` reads),
      // so the global selector dropdown stays populated across reloads.
      // findOrThrow: the `clients` store keeps the library default (throwOnError
      // off), so a failed find resolved null and the picker sat empty with no error.
      await findOrThrow(this.service, { query: { $limit: 200, $sort: { name: 1 } } });
      runInAction(() => {
        const clients = this.clients;
        // If stored client no longer exists, select first
        if (this.selectedClientId && !clients.find((c) => c.id === this.selectedClientId)) {
          if (clients.length > 0) {
            this.setSelectedClientId(clients[0].id);
          } else {
            this.setSelectedClientId(null);
          }
        }
        // If no client selected and we have clients, select first
        if (!this.selectedClientId && clients.length > 0) {
          this.setSelectedClientId(clients[0].id);
        }
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Failed to fetch clients";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async createClient(data: Partial<Client>): Promise<Client | null> {
    this.error = null;
    // A create the sign-out ended RESOLVES null here (the `clients` store keeps
    // throwOnError off): without this the method went on to re-list the
    // clients and re-select one — in the NEXT person's session, writing the
    // pick back to localStorage after the purge had cleared it.
    const ended = sessionMark(this.rootStore);
    try {
      // Create directly against the `clients` service (the `client-onboarding`
      // wrapper service was removed). Build the payload, omitting empty/
      // undefined fields entirely so we never send "" for enum-validated fields
      // like `industry` — the backend treats "", null, and omitted as
      // equivalent for nullable fields, but some strict enum validators reject
      // "" specifically.
      const clientData: Record<string, unknown> = {
        name: data.name,
        code: data.code ? String(data.code).toUpperCase() : undefined,
        fiscalYearEnd: "12-31",
      };
      const optional: Array<keyof Client> = [
        "industry",
        "address",
        "city",
        "state",
        "zip",
        "email",
        "phone",
      ];
      for (const key of optional) {
        const v = data[key];
        if (v != null && v !== "") {
          clientData[key] = v;
        }
      }

      // The `clients` service returns the created client record directly.
      const created = (await this.service?.create(
        clientData as never,
      )) as unknown as Client | undefined;
      if (ended()) return null;

      const createdId = created?.id;
      await this.fetchClients();
      if (createdId) {
        this.setSelectedClientId(createdId);
      }
      return created || null;
    } catch (err: unknown) {
      if (isSessionEnded(err) || ended()) return null;
      // Try to surface feathers-style validation details if present
      let message = "Failed to create client";
      if (err instanceof Error) {
        message = err.message;
      }
      runInAction(() => {
        this.error = message;
      });

      console.error("createClient failed:", err);
      return null;
    }
  }
}

export default ClientStore;
